/** Shared, cross-module form validation helpers (no UI). */

export const GF_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Edit-user email: lowercase letters/numbers only, one dot, no spaces or special chars. */
export const GF_STRICT_UPDATE_EMAIL_REGEX = /^[a-z0-9]+@[a-z0-9]+\.[a-z0-9]+$/;

export function gfValidateStrictUpdateEmail(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "Email is required";
  if (raw !== raw.toLowerCase()) return "Email must use lowercase letters only";
  if (/\s/.test(raw)) return "Spaces are not allowed in email";
  if ((raw.match(/\./g) || []).length !== 1) return "Exactly one dot is allowed in email";
  if (!/^[a-z0-9@.]+$/.test(raw)) {
    return "Email may contain only lowercase letters, numbers, one dot, and @";
  }
  if (!GF_STRICT_UPDATE_EMAIL_REGEX.test(raw)) {
    return "Use format name@domain.ext (example: user@example.com)";
  }
  return "";
}

/** Password rules used by {@link gfValidatePasswordField} / {@link gfIsValidPasswordPolicy}. */
export const GF_PASSWORD_POLICY_MIN_LEN = 8;

export const GF_PASSWORD_POLICY_MAX_LEN = 15;

export const GF_PASSWORD_POLICY_MSG =
  "Password must be 8–15 characters with one capital letter, one number, and one special character (not a space).";

/**
 * Caps password length. Does not strip characters (so users can paste and then edit).
 */
export function gfNormalizePasswordFieldValue(raw) {
  return String(raw ?? "").slice(0, GF_PASSWORD_POLICY_MAX_LEN);
}

/**
 * Validates password policy. Empty string returns "" (handle required in the form).
 */
export function gfValidatePasswordField(value) {
  const p = String(value ?? "");
  if (!p) return "";
  if (p.length < GF_PASSWORD_POLICY_MIN_LEN) {
    return `Use at least ${GF_PASSWORD_POLICY_MIN_LEN} characters.`;
  }
  if (p.length > GF_PASSWORD_POLICY_MAX_LEN) {
    return `Maximum ${GF_PASSWORD_POLICY_MAX_LEN} characters.`;
  }
  if (!/[A-Z]/.test(p)) return "Include at least one capital letter.";
  if (!/[0-9]/.test(p)) return "Include at least one number.";
  if (!/[^A-Za-z0-9\s]/.test(p)) {
    return "Include at least one special character (not a space).";
  }
  return "";
}

export function gfTodayLocalISODate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Max length for `nameTextRules` on `GlobalTextField`. */
export const GF_NAME_TEXT_RULES_MAX_LEN = 40;

/** Minimum letter count (A–Z, a–z) for `nameTextRules` / `gfValidateNameTextField`. */
export const GF_NAME_TEXT_RULES_MIN_LETTERS = 4;

/**
 * Normalizes input for person-style name fields: trims edges, strips digits and
 * disallowed characters (only letters, single spaces, and at most one dot),
 * collapses repeated spaces, caps length at {@link GF_NAME_TEXT_RULES_MAX_LEN}.
 */
export function gfNormalizeNameTextFieldValue(raw) {
  let s = String(raw ?? "");
  s = s.replace(/[0-9]/g, "");
  s = s.replace(/[^a-zA-Z .]/g, "");
  s = s.replace(/\s+/g, " ");
  const dotIdx = s.indexOf(".");
  if (dotIdx !== -1) {
    s = `${s.slice(0, dotIdx + 1)}${s.slice(dotIdx + 1).replace(/\./g, "")}`;
  }
  if (s.length > GF_NAME_TEXT_RULES_MAX_LEN) {
    s = s.slice(0, GF_NAME_TEXT_RULES_MAX_LEN);
  }
  return s;
}

/**
 * Validates a normalized person-style name. Empty string is considered valid
 * (use required handling in the form). Returns a user-facing error message or "".
 */
export function gfValidateNameTextField(value) {
  const v = String(value ?? "").trim();
  if (!v) return "";
  const letters = (v.match(/[a-zA-Z]/g) || []).length;
  if (letters < GF_NAME_TEXT_RULES_MIN_LETTERS) {
    return `Use at least ${GF_NAME_TEXT_RULES_MIN_LETTERS} letters.`;
  }
  if (v.length > GF_NAME_TEXT_RULES_MAX_LEN) {
    return `Maximum ${GF_NAME_TEXT_RULES_MAX_LEN} characters.`;
  }
  if (/[0-9]/.test(v)) return "Numbers are not allowed.";
  if (/[^a-zA-Z .]/.test(v)) {
    return "Only letters, a single space between words, and one dot are allowed.";
  }
  if (/\s{2,}/.test(v)) {
    return "Only a single space is allowed between words.";
  }
  if ((v.match(/\./g) || []).length > 1) {
    return "At most one dot is allowed.";
  }
  return "";
}

/** @deprecated Prefer {@link gfValidateNameTextField} for new code. */
export function gfIsValidDisplayName(name) {
  if (!name || name.length < 4) return false;
  if (/[^a-zA-Z .]/.test(name)) return false;
  if (/\s{2,}/.test(name)) return false;
  if (/\.\./.test(name)) return false;
  return true;
}

export function gfIsValidPasswordPolicy(p) {
  if (!p) return false;
  return gfValidatePasswordField(p) === "";
}

/**
 * Normalizes email input: trims, removes whitespace, keeps a single @, collapses
 * consecutive dots to one.
 */
export function gfNormalizeEmailFieldValue(raw) {
  let s = String(raw ?? "").trim().replace(/\s+/g, "");
  s = s.replace(/\.{2,}/g, ".");
  const at = s.indexOf("@");
  if (at !== -1) {
    s = `${s.slice(0, at + 1)}${s.slice(at + 1).replace(/@/g, "")}`;
  }
  return s;
}

/**
 * Validates email: one @, no consecutive dots, basic format. Empty → "".
 */
export function gfValidateEmailField(value) {
  const s = String(value ?? "").trim();
  if (!s) return "";
  if ((s.match(/@/g) || []).length !== 1) {
    return "Use exactly one @.";
  }
  if (/\.\./.test(s)) {
    return "Do not use two dots in a row.";
  }
  if (!GF_EMAIL_REGEX.test(s)) {
    return "Enter a valid email address.";
  }
  return "";
}

/**
 * Strips minus signs and non-numeric characters except one decimal point (for amounts).
 */
export function gfNormalizeUnsignedNumberValue(raw) {
  let s = String(raw ?? "").replace(/[-+]/g, "");
  s = s.replace(/[^0-9.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = `${s.slice(0, firstDot + 1)}${s.slice(firstDot + 1).replace(/\./g, "")}`;
  }
  return s;
}

/** Empty → "". Otherwise rejects negatives (after normalize, minus should not appear). */
export function gfValidateUnsignedNumber(value) {
  const s = String(value ?? "").trim();
  if (!s) return "";
  if (s.includes("-") || s.startsWith("+")) {
    return "Negative numbers are not allowed.";
  }
  const n = Number(s);
  if (Number.isNaN(n)) return "Enter a valid number.";
  if (n < 0) return "Negative numbers are not allowed.";
  return "";
}

export function gfIsISODateOnOrBeforeToday(isoDate) {
  if (!isoDate) return false;
  const day = String(isoDate).trim().slice(0, 10);
  if (Number.isNaN(Date.parse(day))) return false;
  return day <= gfTodayLocalISODate();
}

export function gfIsValidISODate(isoDate) {
  if (!isoDate) return false;
  const day = String(isoDate).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;
  return !Number.isNaN(Date.parse(day));
}

export function gfIsISODateOnOrAfter(isoDate, minIsoDate) {
  if (!gfIsValidISODate(isoDate)) return false;
  if (!gfIsValidISODate(minIsoDate)) return false;
  return String(isoDate).slice(0, 10) >= String(minIsoDate).slice(0, 10);
}

export function gfIsISODateOnOrBefore(isoDate, maxIsoDate) {
  if (!gfIsValidISODate(isoDate)) return false;
  if (!gfIsValidISODate(maxIsoDate)) return false;
  return String(isoDate).slice(0, 10) <= String(maxIsoDate).slice(0, 10);
}

export function gfIsValidISODateRange(fromIsoDate, toIsoDate) {
  if (!fromIsoDate && !toIsoDate) return true;
  if (fromIsoDate && !gfIsValidISODate(fromIsoDate)) return false;
  if (toIsoDate && !gfIsValidISODate(toIsoDate)) return false;
  if (fromIsoDate && toIsoDate) {
    return String(fromIsoDate).slice(0, 10) <= String(toIsoDate).slice(0, 10);
  }
  return true;
}

export function gfIsRequiredTrimmedString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

export function gfIsRequiredPositiveIntId(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0;
}

export function gfIsNonNegativeNumber(value) {
  if (value === "" || value === null || value === undefined) return false;
  const n = Number(value);
  return !Number.isNaN(n) && n >= 0;
}

